import { useEffect, useState } from "react";
import {
  Typography, Button, Box, TextField, Chip, InputAdornment, Avatar, Paper, Autocomplete, Alert,
  Radio, RadioGroup, FormControlLabel, IconButton, CircularProgress, Stack
} from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LinkIcon from '@mui/icons-material/Link';
import AddIcon from '@mui/icons-material/Add';
import PropTypes from "prop-types";

import { buscarPersonas } from "../../../actions/users";
import { urlImg, textoDuracion } from "../../common/helpers";
import { nombreDe, fotoDe, ESTADO_FACILITADOR } from "../../common/imparte";
import { dinero } from "../../../utils/dinero";

// Un servicio del dueño dentro del formulario de su página: añadirlo o quitarlo y, en un grupo o negocio, decidir QUIÉN LO IMPARTE.
// Todo se decide aquí mismo: «Yo» (por omisión) u otra persona, que se busca por nombre o correo. La invitación sale al guardar la
// página y el estado (pendiente, aceptó, rechazó) se ve junto al servicio. Mientras la persona no acepta, el servicio no se puede
// contratar: se dice aquí para que no sea una sorpresa.
const ServicioDePagina = ({
  service, fuera = false, agregado, esGrupo, duenoId, elegido = null, estado = null, equipo = [], recortados = 0,
  puedeReenviar = false, reenviando = false, onToggle, onElegir, onReenviar, onVer
}) => {
  // «Otra persona» marcado aunque todavía no se haya elegido a nadie
  const [quiereOtra, setQuiereOtra] = useState(false);
  const [texto, setTexto] = useState("");
  const [resultados, setResultados] = useState([]);
  const [buscando, setBuscando] = useState(false);

  // Búsqueda con una pequeña espera para no pedir en cada tecla
  useEffect(() => {
    const limpio = texto.trim();
    if (limpio.length < 3) { setResultados([]); setBuscando(false); return undefined; }
    let vigente = true;
    setBuscando(true);
    const espera = setTimeout(async () => {
      const personas = await buscarPersonas(limpio);
      if (vigente) { setResultados(personas); setBuscando(false); }
    }, 300);
    return () => { vigente = false; clearTimeout(espera); };
  }, [texto]);

  const mainImg = service.img && service.img.length > 0 ? service.img[0].nombre : null;
  const imgUrl = mainImg
    ? `${urlImg}/servicios/${service._id}/${mainImg}`
    : "https://via.placeholder.com/150";
  const duracion = textoDuracion(service.duracionEstimada, service.unidadDuracion);

  // Primero quienes ya están en el equipo de la página; después lo que devuelve la búsqueda. Nunca el dueño ni repetidos.
  const opciones = [];
  [...equipo, ...resultados].forEach((p) => {
    if (!p || !p._id || String(p._id) === String(duenoId)) return;
    if (!opciones.some((o) => String(o._id) === String(p._id))) opciones.push(p);
  });

  const modo = elegido || quiereOtra ? "otra" : "yo";
  const nombre = nombreDe(elegido) || "esa persona";
  const meta = estado ? ESTADO_FACILITADOR[estado] : null;

  const cambiarModo = (valor) => {
    if (valor === "yo") { setQuiereOtra(false); setTexto(""); onElegir(null); } else setQuiereOtra(true);
  };

  const elegir = (persona) => {
    setTexto("");
    if (persona) onElegir(persona);
  };

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        mb: 2,
        borderRadius: "12px",
        display: "flex",
        flexDirection: { xs: "column", sm: "row" },
        gap: 2,
        transition: "box-shadow 0.3s ease",
        borderColor: fuera ? "warning.main" : undefined,
        "&:hover": {
          boxShadow: "0 4px 20px rgba(0,0,0,0.08)"
        }
      }}
    >
      <Box
        sx={{
          width: { xs: "100%", sm: 200 },
          height: 150,
          borderRadius: "8px",
          overflow: "hidden",
          flexShrink: 0
        }}
      >
        <img
          src={imgUrl}
          alt={service.nombre}
          style={{ width: "100%", height: "100%", objectFit: "cover" }}
        />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 2, mb: 1 }}>
          <Box sx={{ minWidth: 0 }}>
            <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", columnGap: 1 }}>
              <Typography variant="subtitle1" color="primary" fontWeight="bold">
                {service.nombre}
              </Typography>
              {fuera && (
                <Chip size="small" label="Fuera de la categoría" sx={{ bgcolor: "warning.main", color: "primary.main", fontWeight: 600 }} />
              )}
            </Box>
            <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
              {service.descripcion}
            </Typography>
          </Box>
          <Box sx={{ textAlign: "right", flexShrink: 0 }}>
            <Typography variant="h6" color="primary">
              {dinero(service.costoServicio)}
            </Typography>
            <Typography variant="caption">
              por {service.tipoCosto || "servicio"}
            </Typography>
          </Box>
        </Box>

        <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
          {duracion && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <AccessTimeIcon sx={{ fontSize: 18, color: "primary.main" }} />
              <Typography variant="caption">Duración: {duracion}</Typography>
            </Box>
          )}
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <LocationOnIcon sx={{ fontSize: 18, color: "primary.main" }} />
            <Typography variant="caption">
              {service.modalidades?.length ? service.modalidades.join(", ") : "Presencial"}
            </Typography>
          </Box>
        </Stack>

        <Box sx={{ mt: "auto", display: "flex", justifyContent: "flex-end", gap: 1 }}>
          <Button
            variant="contained"
            color="success"
            size="small"
            startIcon={<LinkIcon />}
            sx={{ color: "white", textTransform: "none", borderRadius: "8px" }}
            onClick={onVer}
          >
            Ver
          </Button>
          <Button
            variant="contained"
            size="small"
            color={agregado ? "error" : "primary"}
            startIcon={agregado ? <CloseIcon /> : <AddIcon />}
            sx={{ color: "white", textTransform: "none", borderRadius: "8px" }}
            onClick={onToggle}
          >
            {agregado ? "Quitar" : "Añadir +"}
          </Button>
        </Box>

        {esGrupo && agregado && (
          <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: "sutil" }}>
            <Typography variant="body2" color="primary" fontWeight="bold">
              ¿Quién lo imparte?
            </Typography>
            <RadioGroup row value={modo} onChange={(e) => cambiarModo(e.target.value)} aria-label={`Quién imparte ${service.nombre}`}>
              <FormControlLabel value="yo" control={<Radio size="small" />} label={<Typography variant="body2">Yo</Typography>} />
              <FormControlLabel value="otra" control={<Radio size="small" />} label={<Typography variant="body2">Otra persona</Typography>} />
            </RadioGroup>

            {modo === "otra" && !elegido && (
              <Autocomplete
                size="small"
                options={opciones}
                getOptionLabel={(o) => `${nombreDe(o)}${o.email ? ` (${o.email})` : ""}`}
                isOptionEqualToValue={(o, v) => String(o._id) === String(v._id)}
                value={null}
                inputValue={texto}
                onInputChange={(_, valor, motivo) => { if (motivo !== "reset") setTexto(valor); }}
                onChange={(_, persona) => elegir(persona)}
                loading={buscando}
                loadingText="Buscando…"
                noOptionsText={texto.trim().length >= 3 ? "No se encontró a nadie" : "Escribe al menos 3 letras"}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Busca por nombre o correo"
                    variant="outlined"
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <InputAdornment position="start">
                          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 'bold' }}>@</Typography>
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <>
                          {buscando ? <CircularProgress color="inherit" size={16} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <li {...props} key={option._id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                      <Avatar src={fotoDe(option, 'w_40,h_40,c_fill')} sx={{ width: 24, height: 24 }} />
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" noWrap>{nombreDe(option)}</Typography>
                        {option.email && <Typography variant="caption" color="text.secondary" noWrap>{option.email}</Typography>}
                      </Box>
                    </Box>
                  </li>
                )}
              />
            )}

            {elegido && (
              <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1, mb: 1 }}>
                <Avatar src={fotoDe(elegido, 'w_40,h_40,c_fill')} sx={{ width: 32, height: 32 }} />
                <Typography variant="body2" color="primary" fontWeight="bold">{nombreDe(elegido)}</Typography>
                {meta && (
                  <Chip size="small" label={meta.etiqueta} sx={{ bgcolor: meta.color, color: "primary.main", fontWeight: 700 }} />
                )}
                <IconButton size="small" aria-label={`Quitar a ${nombre}`} onClick={() => { setQuiereOtra(true); onElegir(null); }}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            )}

            {elegido && !estado && (
              <Alert severity="info" sx={{ borderRadius: "12px" }}>
                {nombre} recibirá la invitación cuando guardes la página. Mientras no la acepte, este servicio no se podrá contratar.
              </Alert>
            )}
            {elegido && estado === "pendiente" && (
              <Alert
                severity="warning"
                sx={{ borderRadius: "12px" }}
                action={puedeReenviar && (
                  <Button color="inherit" size="small" disabled={reenviando} onClick={onReenviar} sx={{ textTransform: "none", fontWeight: 700, whiteSpace: "nowrap" }}>
                    Reenviar invitación
                  </Button>
                )}
              >
                Invitación enviada. Mientras {nombre} no la acepte, este servicio no se puede contratar.
              </Alert>
            )}
            {elegido && estado === "aceptado" && (
              <Alert severity="success" sx={{ borderRadius: "12px" }}>
                {nombre} aceptó: aparece como quien imparte este servicio.
              </Alert>
            )}
            {elegido && estado === "rechazado" && (
              <Alert severity="error" sx={{ borderRadius: "12px" }}>
                {nombre} rechazó la invitación. El servicio no se puede contratar hasta que elijas a otra persona o lo impartas tú.
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
                  <Button variant="contained" size="small" onClick={() => cambiarModo("yo")} sx={{ textTransform: "none" }}>
                    Impartirlo yo
                  </Button>
                  <Button variant="outlined" size="small" onClick={() => { setQuiereOtra(true); onElegir(null); }} sx={{ textTransform: "none" }}>
                    Elegir a otra persona
                  </Button>
                  {puedeReenviar && (
                    <Button size="small" disabled={reenviando} onClick={onReenviar} sx={{ textTransform: "none" }}>
                      Invitar de nuevo
                    </Button>
                  )}
                </Box>
              </Alert>
            )}

            {!elegido && modo === "yo" && (
              <Typography variant="caption" color="text.secondary">
                Lo impartes tú. Si lo va a dar alguien más de tu equipo, elige «Otra persona».
              </Typography>
            )}
            {recortados > 1 && (
              <Alert severity="warning" sx={{ mt: 1, borderRadius: "12px" }}>
                Este servicio tenía {recortados} facilitadores y ahora solo puede tener uno: se dejó al primero.
                {" "}Cámbialo si prefieres a otro y guarda la página.
              </Alert>
            )}
          </Box>
        )}
      </Box>
    </Paper>
  );
};

ServicioDePagina.propTypes = {
  service: PropTypes.object.isRequired,
  fuera: PropTypes.bool,
  agregado: PropTypes.bool.isRequired,
  esGrupo: PropTypes.bool.isRequired,
  duenoId: PropTypes.string,
  elegido: PropTypes.object,
  estado: PropTypes.string,
  equipo: PropTypes.array,
  recortados: PropTypes.number,
  puedeReenviar: PropTypes.bool,
  reenviando: PropTypes.bool,
  onToggle: PropTypes.func.isRequired,
  onElegir: PropTypes.func.isRequired,
  onReenviar: PropTypes.func.isRequired,
  onVer: PropTypes.func.isRequired,
};

export default ServicioDePagina;
